import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight, Lock, Mail, ShieldCheck, Check, FileText } from "lucide-react";
import { api } from "../lib/api";
import { trackInitiateCheckout, trackViewContent, identifyUser } from "../lib/tiktok";

const PRICE = "$79";
const FONT = "'Inter','SF Pro Display',system-ui,-apple-system,sans-serif";
const SERIF = "'Fraunces','Playfair Display',Georgia,serif";

const ensureFonts = () => {
  if (document.getElementById("ipo-font")) return;
  const l = document.createElement("link");
  l.id = "ipo-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&family=Fraunces:wght@400;500;600;700&display=swap";
  document.head.appendChild(l);
};

// Google Ads — IPO Access Dossier "Add to cart" conversion. Fires once per
// session when the buyer commits to checkout (email submitted, redirecting
// to Stripe). Mirrors the timing of TikTok InitiateCheckout.
const fireGoogleAdsAddToCart = () => {
  try {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    window.gtag("event", "conversion", {
      send_to: "AW-18239642780/L2brCPLJhL8cEJy5q_lD",
      value: 1.0,
      currency: "EGP",
    });
  } catch (_) { /* analytics must never break the funnel */ }
};

// First-party funnel analytics — beacon survives the redirect to Stripe.
const vid = () => {
  try {
    let v = localStorage.getItem("ipo_vid");
    if (!v) {
      v = window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Math.random().toString(36).slice(2)}${Date.now()}`;
      localStorage.setItem("ipo_vid", v);
    }
    return v;
  } catch (_) { return "anon"; }
};
const track = (event, props = {}) => {
  try {
    const payload = JSON.stringify({ event, props, visitor_id: vid(), page: "ipocheckout" });
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/ipo/track`;
    if (navigator.sendBeacon) navigator.sendBeacon(url, payload);
    else api.post("/ipo/track", JSON.parse(payload));
  } catch (_) { /* analytics must never break the funnel */ }
};

export default function IPOCheckout() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [emailErr, setEmailErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);
  // Prevents double-firing Google Ads / TikTok if the user clicks Continue
  // twice before the Stripe redirect lands.
  const addToCartFiredRef = useRef(false);

  useEffect(() => {
    ensureFonts();
    document.title = "Secure Checkout — Wealth Access Dossier";
    trackViewContent("ipo_access_dossier");
    track("cta_click", { target: "checkout-view" });
  }, []);

  // Auto-focus the email input on desktop only (mobile keyboards stealing focus
  // mid-scroll is annoying — let the user reach the form on their own).
  useEffect(() => {
    if (window.matchMedia("(min-width: 768px)").matches) {
      setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, []);

  const startCheckout = async (e) => {
    if (e) e.preventDefault();
    setErr("");
    const trimmed = (email || "").trim().toLowerCase();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed);
    if (!valid) {
      setEmailErr("Enter a valid email to receive your dossier.");
      inputRef.current?.focus();
      return;
    }
    setEmailErr("");
    setLoading(true);
    identifyUser({ email: trimmed, externalId: vid() });
    if (!addToCartFiredRef.current) {
      addToCartFiredRef.current = true;
      trackInitiateCheckout("ipo_access_dossier");
      fireGoogleAdsAddToCart();
    }
    track("email_submit");
    track("cta_click", { target: "stripe-checkout" });
    try {
      const { data } = await api.post("/ipo/checkout", { email: trimmed, visitor_id: vid() });
      window.location.href = data.checkout_url;
    } catch (e2) {
      const detail = e2?.response?.data?.detail || "Could not start checkout. Please try again in a moment.";
      setErr(detail);
      setLoading(false);
    }
  };

  return (
    <div
      dir="ltr"
      lang="en"
      className="min-h-screen bg-[#0A0D12] text-[#E7EAF0] selection:bg-[#3F8EFC]/40"
      style={{ fontFamily: FONT, textAlign: "left" }}
      data-testid="ipo-checkout-root"
    >
      {/* NAV */}
      <nav className="px-5 pt-5 max-w-5xl mx-auto flex items-center justify-between">
        <Link to="/ipoaccess" className="flex items-center gap-2.5" data-testid="ipo-checkout-logo">
          <img src="/ipo-access-dossier-logo.jpg" alt="IPO Access Dossier" className="h-9 w-9 rounded-sm object-cover" />
          <div className="leading-tight">
            <div className="font-bold text-[13px] tracking-tight">IPO Access Dossier<span className="text-[#3F8EFC]">™</span></div>
            <div className="text-[9.5px] text-[#7A8597] tracking-[0.18em]">EARLY ACCESS · INSIGHT · ADVANTAGE</div>
          </div>
        </Link>
        <span className="text-[11px] text-[#7A8597] flex items-center gap-1.5">
          <Lock className="w-3.5 h-3.5 text-[#3F8EFC]" /> Secure checkout
        </span>
      </nav>

      {/* Hairline progress strip */}
      <div className="max-w-5xl mx-auto px-5 mt-6" data-testid="ipo-checkout-progress">
        <div className="flex items-center text-[10.5px] tracking-[0.22em] font-bold text-[#7A8597]">
          <span className="text-[#3F8EFC]">1. REVIEW</span>
          <span className="flex-1 mx-3 h-px bg-gradient-to-r from-[#3F8EFC]/60 to-white/[0.06]" />
          <span className="text-[#3F8EFC]">2. EMAIL</span>
          <span className="flex-1 mx-3 h-px bg-white/[0.06]" />
          <span>3. PAYMENT</span>
        </div>
      </div>

      <main className="px-5 py-10 sm:py-14">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-12">
          {/* LEFT — sales pitch (user-supplied verbatim, "Wealth Access Dossier" branding) */}
          <article className="order-2 lg:order-1" data-testid="ipo-checkout-pitch">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-sm bg-[#3F8EFC]/[0.08] border border-[#3F8EFC]/30 text-[10.5px] tracking-[0.22em] text-[#3F8EFC] font-bold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#3F8EFC] animate-pulse" /> WEALTH ACCESS DOSSIER
            </div>

            <h1
              className="font-bold text-[30px] sm:text-[40px] leading-[1.1] tracking-tight text-white"
              style={{ fontFamily: SERIF, fontWeight: 600 }}
              data-testid="ipo-checkout-title"
            >
              You&apos;re not buying <span className="text-[#7A8597] italic">&ldquo;information&rdquo;.</span>
            </h1>
            <p className="mt-5 text-[15.5px] text-[#D6DCE7] leading-[1.8]">
              You&apos;re buying the <span className="text-white font-semibold">edge before the crowd turns it into noise.</span>
            </p>
            <p className="mt-4 text-[14.5px] text-[#A5AFC0] leading-[1.8]">
              Most people only notice a money opportunity when everyone is already talking about it.
            </p>
            <p className="mt-3 text-[14.5px] text-[#A5AFC0] leading-[1.8]">
              By then, the clean move is usually gone.
            </p>
            <p className="mt-6 text-[15px] text-[#E7EAF0] leading-[1.8]">
              <span className="font-bold text-white">Wealth Access Dossier</span> is built for one purpose:
            </p>
            <p className="mt-2 text-[14.5px] text-[#A5AFC0] leading-[1.8]">
              To help you <span className="text-white font-semibold">see the opportunity, timing, risk, and edge before you make your next move.</span>
            </p>

            {/* What you get */}
            <div className="mt-10">
              <h2 className="font-bold text-[20px] sm:text-[22px] text-white tracking-tight" style={{ fontFamily: SERIF, fontWeight: 600 }}>
                What you get instantly
              </h2>

              <div className="mt-5 bg-[#0E1218] border border-[#3F8EFC]/30 rounded-sm p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="w-11 h-11 rounded-sm bg-gradient-to-br from-[#3F8EFC] to-[#1E5BD8] flex items-center justify-center shrink-0">
                    <FileText className="w-4.5 h-4.5 text-white" strokeWidth={2} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10.5px] tracking-[0.24em] text-[#3F8EFC] font-bold mb-1">PREMIUM</div>
                    <div className="font-bold text-[16px] sm:text-[17px] text-white leading-tight">
                      Wealth Access Dossier — $79 One-Time
                    </div>
                    <div className="text-[12.5px] text-[#7A8597] mt-1">Private-style opportunity intelligence file</div>
                  </div>
                </div>

                <p className="mt-5 text-[13.5px] text-[#A5AFC0] leading-[1.75]">
                  A private-style opportunity intelligence file designed to show you:
                </p>

                <ul className="mt-4 space-y-2.5 text-[14px] text-[#E7EAF0] leading-relaxed">
                  {[
                    ["What the opportunity actually is", "RIGHT NOW"],
                    ["Why people are", "starting to notice it"],
                    ["Where the", "real edge may be"],
                    ["What the crowd is", "likely missing"],
                    ["What could", "go wrong"],
                    ["Whether the timing", "still looks attractive"],
                    ["How to think before acting,", "instead of reacting"],
                  ].map(([head, tail], i) => (
                    <li key={i} className="flex items-start gap-3" data-testid={`ipo-pitch-point-${i}`}>
                      <Check className="w-4 h-4 text-[#3F8EFC] shrink-0 mt-1" strokeWidth={2.6} />
                      <span><span className="text-white font-semibold">{head}</span> <span className="text-[#A5AFC0]">{tail}</span></span>
                    </li>
                  ))}
                </ul>
              </div>

              <p className="mt-6 text-[14px] text-[#A5AFC0] leading-[1.8]">
                This is <span className="text-white">not a generic guide.</span>
              </p>
              <p className="mt-3 text-[14px] text-[#A5AFC0] leading-[1.8]">
                This is a <span className="text-white font-semibold">premium, structured intelligence dossier</span> built to help you avoid being the person who discovers the move after the money has already moved.
              </p>
            </div>

            {/* Why this matters */}
            <div className="mt-10" data-testid="ipo-checkout-why">
              <h2 className="font-bold text-[20px] sm:text-[22px] text-white tracking-tight" style={{ fontFamily: SERIF, fontWeight: 600 }}>
                Why this matters
              </h2>
              <div className="mt-4 border-l-2 border-[#3F8EFC]/60 pl-5 space-y-3">
                <p className="text-[15px] text-[#E7EAF0] leading-[1.75]">The crowd does not find opportunities.</p>
                <p className="text-[15px] text-[#E7EAF0] leading-[1.75]">The crowd finds <span className="text-[#7A8597] italic">leftovers</span>.</p>
                <p className="text-[16px] text-white font-semibold leading-[1.6]" style={{ fontFamily: SERIF, fontWeight: 600 }}>
                  Get the edge first.
                </p>
              </div>
            </div>
          </article>

          {/* RIGHT — sticky order summary + email gate */}
          <aside className="order-1 lg:order-2" data-testid="ipo-checkout-form-col">
            <div className="lg:sticky lg:top-6">
              <div className="bg-[#0E1218] border border-[#3F8EFC]/40 rounded-sm p-6 shadow-[0_30px_80px_-25px_rgba(63,142,252,0.5)]" data-testid="ipo-checkout-card">
                {/* Order line */}
                <div className="flex items-start justify-between pb-4 border-b border-white/[0.07]">
                  <div className="min-w-0 pr-3">
                    <div className="text-[10px] tracking-[0.24em] text-[#7A8597] font-bold">YOUR ORDER</div>
                    <div className="mt-1 font-bold text-[15px] text-white leading-tight">Wealth Access Dossier</div>
                    <div className="text-[11.5px] text-[#7A8597] mt-0.5">Instant digital delivery (PDF)</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-3xl font-bold text-white tabular-nums" style={{ fontFamily: SERIF, fontWeight: 600 }}>$79</div>
                    <div className="text-[10.5px] text-[#7A8597] tracking-wide">USD · one-time</div>
                  </div>
                </div>

                {/* Email form */}
                <form onSubmit={startCheckout} className="mt-5" data-testid="ipo-checkout-form">
                  <label htmlFor="ipo-email" className="text-[10.5px] tracking-[0.22em] text-[#7A8597] font-bold mb-2 block">
                    YOUR EMAIL
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#7A8597]" />
                    <input
                      id="ipo-email"
                      ref={inputRef}
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (emailErr) setEmailErr(""); }}
                      placeholder="you@example.com"
                      aria-label="Your email"
                      data-testid="ipo-checkout-email-input"
                      className="w-full bg-[#0A0D12] border border-white/[0.10] focus:border-[#3F8EFC] focus:ring-2 focus:ring-[#3F8EFC]/30 outline-none rounded-sm pl-10 pr-3 py-3.5 text-[15px] text-white placeholder:text-[#5A6275] transition-all min-h-[54px]"
                    />
                  </div>
                  <div className="mt-2 min-h-[18px]">
                    {emailErr ? (
                      <div className="text-[12px] text-[#F0838F]" data-testid="ipo-checkout-email-err">{emailErr}</div>
                    ) : (
                      <div className="text-[11.5px] text-[#7A8597]">Your dossier is sent here and accessible on the next page.</div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="ipo-checkout-submit"
                    className="mt-5 w-full bg-[#3F8EFC] hover:bg-[#5BA0FF] text-white font-bold text-[15px] tracking-wide px-7 py-4 rounded-sm transition-all disabled:opacity-60 flex items-center justify-center gap-2 min-h-[56px] shadow-[0_10px_36px_-10px_rgba(63,142,252,0.65)]"
                  >
                    {loading ? "Opening secure checkout…" : <>Continue to Payment — {PRICE} <ArrowRight className="w-4 h-4" /></>}
                  </button>

                  {err && (
                    <div className="mt-3 text-[12.5px] text-[#F0838F]" data-testid="ipo-checkout-err">{err}</div>
                  )}

                  <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-[#7A8597]">
                    <Lock className="w-3 h-3 text-[#3F8EFC]" /> Card &amp; wallet entered on Stripe&apos;s secure page
                  </div>
                </form>

                {/* Trust strip */}
                <ul className="mt-6 pt-5 border-t border-white/[0.07] space-y-2.5 text-[12.5px] text-[#A5AFC0]">
                  {[
                    "One-time purchase — no subscription",
                    "Instant access after payment",
                    "Independent institutional-style research",
                  ].map((t) => (
                    <li key={t} className="flex items-center gap-2.5">
                      <ShieldCheck className="w-3.5 h-3.5 text-[#3F8EFC] shrink-0" strokeWidth={2} /> {t}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-4 text-center text-[11px] text-[#5A6275]">
                Have a question? <a href="mailto:support@replyrescue.app" className="text-[#7A8597] hover:text-[#3F8EFC]">support@replyrescue.app</a>
              </div>
            </div>
          </aside>
        </div>
      </main>

      {/* FOOTER */}
      <footer className="px-5 py-8 border-t border-white/[0.06] text-center mt-6" data-testid="ipo-checkout-footer">
        <p className="text-[11px] text-[#5A6275] max-w-lg mx-auto leading-relaxed">
          Wealth Access Dossier (IPO Access Dossier™) is an independent research product. Nothing in this product constitutes investment, legal, or tax advice. Investing in IPO and pre-IPO opportunities involves significant risk, including total loss of capital.
        </p>
        <p className="mt-3 text-[10.5px] text-[#5A6275] tracking-wide" data-testid="ipo-checkout-footer-disclaimer">
          Educational research only. No financial advice. No guaranteed results.
        </p>
      </footer>
    </div>
  );
}
